import { computed, inject, Injectable, signal } from '@angular/core';
import { isEqual } from 'lodash-es';
import { WidgetMappedType, WidgetStructureObject } from '../../api/interfaces';
import { ClipboardService } from '../../pages/new-builder-dashboard/clipboard.service';
import { BuilderService } from './builder.service';
import { SubSectionService } from './sub-section.service';

@Injectable()
export class WidgetService<T = unknown> {
  private readonly builderService = inject(BuilderService);
  private readonly subsectionService = inject(SubSectionService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly _uniqueId = signal<string | undefined>(undefined);

  get uniqueId(): string | undefined {
    return this._uniqueId();
  }

  set uniqueId(uniqueId: string) {
    this._uniqueId.set(uniqueId);
  }

  widget = computed(() => {
    const id = this._uniqueId();
    if (!id) return undefined;

    const tmp = this.builderService.moduleStructure()[
      id
    ] as WidgetStructureObject<T>;
    return tmp;
  });

  settingVisibility = signal(false);

  pasteEnabled = computed(() => {
    return this.clipboardService.isWidgetStructure();
  });

  widgetErrors = computed(
    () => {
      const uniqueId = this._uniqueId();
      if (!uniqueId) return { missingIds: [], type: 'section' };

      const errors = this.builderService.errors();
      return errors[uniqueId];
    },
    { equal: isEqual },
  );

  readonly maxPoints = computed(() => {
    const uniqueId = this._uniqueId();
    if (!uniqueId) return 0;
    return this.builderService.getMaxPointsForWidget(uniqueId)();
  });

  readonly computedDescription = computed(() => {
    const widget = this.widget();
    if (!widget) return '';

    const description = widget.customization.description ?? '';
    const maxPointTypes: WidgetMappedType[] = [
      'TextBox',
      'RadioButton',
      'MultiplierRadioButton',
    ];
    const pointTypes: WidgetMappedType[] = ['CheckBox'];

    const maxPoints = this.maxPoints();
    if (maxPointTypes.includes(widget._mappedType)) {
      return `${description} (max ${maxPoints} pt)`;
    }

    if (pointTypes.includes(widget._mappedType)) {
      return `${description} (${maxPoints} pt)`;
    }

    return description;
  });

  readonly baseElementShowDescription = computed(() => {
    const widget = this.widget();
    if (!widget) return true;

    const excludedTypes: WidgetMappedType[] = ['CheckBox', 'StaticText'];
    return !excludedTypes.includes(widget._mappedType);
  });

  removeWidget() {
    const id = this._uniqueId();
    if (!id) return;

    this.subsectionService.removeWidget(id);
  }

  copyWidget() {
    const widget = this.widget();
    if (!widget) return;
    this.clipboardService.copy(widget);
  }

  pasteWidget() {
    const subSectionId = this.subsectionService.uniqueId;
    if (!subSectionId) return;

    this.clipboardService.paste(subSectionId);
  }

  updateWidget(newWidgetId: string, widgetData: T) {
    const subSectionId = this.subsectionService.uniqueId;
    if (!subSectionId) return;

    const currentWidgetId = this._uniqueId();
    if (!currentWidgetId) return;

    const widget = this.widget();
    if (!widget) return;

    this.builderService.updateWidget(
      subSectionId,
      currentWidgetId,
      newWidgetId,
      widgetData,
      widget._mappedType,
    );
  }

  getWeightedPoints(weight: number) {
    return computed(() => {
      const uniqueId = this._uniqueId();
      if (!uniqueId) return 0;
      return this.builderService.getWeightedPointsForWidget(uniqueId, weight)();
    });
  }
}
